import { Coms4csState } from '@mfe-4cs/classes/Coms4csState';
import { renderedPortalCache } from '@mfec/customerAttributes/renderedPortalCache';
import {
  observable, action, computed, autorun, makeObservable, runInAction, reaction,
} from 'mobx';
import { isUuid, isReference } from '@mfe-4cs/util/validate';
import { assert } from '@mfe-4cs/util/assert';
import { Identity, ConsentGroup } from '@mfe-4cs/types/comsApi';
import { ApiLoadConsentResponse, PageDetails, TefComs } from '@mfe-4cs/types/typings';
import Service from '@mfe-4cs/util/Service';
import Tracker from '@mfe-4cs/classes/Tracker';
import { CustomerAttribute, CustomerAttributeSave } from '@mfec/customerAttributes/types';
import { customerAttributesDiffer } from '@mfec/customerAttributes/customerAttributesDiffer';
import { mapCustomerAttributeTypeToSaveType } from '@mfec/customerAttributes/mapCustomerAttributeTypeToSaveType';
import { customerAttributesChanges } from '@mfe-4cs/util/changeDetection';
import ClientStore from './ClientStore';
import { ConsentGroupStore } from './ConsentGroupStore';
import OcclStore from './OcclStore';
import { ConsentStore } from './ConsentStore';
import { HistoryStore } from './HistoryStore';

export interface ComsStorePersist {
  identity: Identity;
  config: TefComs.ConfigOptions;
  page: ApiLoadConsentResponse[];
}

export class ComsStore {
  public consentGroups?: ConsentGroupStore[] = undefined;
  public customerAttributes?: CustomerAttribute[] = [];

  public touched = false;

  public details?: PageDetails = undefined;
  public identity?: Identity = undefined;

  public config?: TefComs.ConfigOptions = undefined;
  public consentWhitelist: string[] | undefined = undefined;
  public client?: ClientStore;
  public history?: HistoryStore;
  public occl?: OcclStore;
  public fromStorage = false;

  public references: TefComs.Reference[] = [];
  public showReferences = false;
  public showVersions = false;
  public isExistingRegister = false;
  public isNewCustomer = false;

  public service = new Service();
  public tracker = new Tracker();

  public error?: Error = undefined;

  private coms4csState: Coms4csState;

  constructor() {
    makeObservable(this, {
      consentGroups: observable,
      customerAttributes: observable,
      setCustomerAttribute: action,
      identity: observable,
      error: observable,
      references: observable,
      fatal: computed,
      init: action,
      undoAll: action,
      undo: action,
      loadSnapshot: action,
      isDataLoaded: computed,
      isOcclReady: computed,
      touched: observable,
      hasModifiedFromOriginal: computed,
      hasModifiedFromInitial: computed,
      modifiedConsentsGranted: computed,
      modifiedConsentsRevoked: computed,
      modifiedCustomerAttributesGranted: computed,
      modifiedCustomerAttributesRevoked: computed,
      changesInConsentsMarker: computed,
      changesEverywhereMarker: computed,
      allConsentsRevokedOrGranted: computed,
      consentsStatusMessage: computed,
    });

    this.coms4csState = new Coms4csState(this);
  }

  /**
   * Determines whether there have been modifications from the original state.
   *
   * This method evaluates if there are any changes to the consents (granted or revoked)
   * or any differences in customer attributes compared to their original values.
   *
   * @return {boolean} True if modifications have been detected; false otherwise.
   */
  get hasModifiedFromOriginal() {
    const consentsAreModified = this.modifiedConsentsGranted > 0 || this.modifiedConsentsRevoked > 0;
    const customerAttributesModified = customerAttributesDiffer(
      this.customerAttributes,
      this.customerAttributesOrigin,
    );
    return consentsAreModified || customerAttributesModified;
  }

  /**
   * Determines whether the current state has been modified from its initial state.
   *
   * Compares the `customerAttributes` with their origin and evaluates if any consents
   * have been modified from their initial state or their related features.
   *
   * @return {boolean} True if the current state is modified from the initial state, otherwise false.
   */
  get hasModifiedFromInitial() {
    const customerAttributesModified = customerAttributesDiffer(
      this.customerAttributes,
      this.customerAttributesOrigin,
    );
    return customerAttributesModified || this.consents.some((c) => c.modifiedFromInitialSelfOrOneOfTheFeatures);
  }

  /**
   * Retrieves the total count of consents with the 'granted' state that have been modified.
   * Iterates through the `consents` collection, summing up the modified counts for 'granted' status.
   *
   * @return {number} The total count of modified consents with the 'granted' state.
   */
  get modifiedConsentsGranted() {
    return this.consents.reduce((counter, consent) => counter + consent.countModified('granted'), 0);
  }

  /**
   * Calculates the total number of modified consents with a status of "revoked."
   *
   * Iterates through all consents, invoking the `countModified` method for each
   * consent with "revoked" as the parameter. Sums up and returns the total count.
   *
   * @return {number} The total count of consents with a "revoked" modification status.
   */
  get modifiedConsentsRevoked() {
    return this.consents.reduce((counter, consent) => counter + consent.countModified('revoked'), 0);
  }

  /**
   * Calculates a marker value based on the sum of modified consents granted and modified consents revoked,
   * with the consents revoked being multiplied by 100.
   *
   * @return {number} The calculated marker value derived from the modified consents granted and revoked.
   */
  get changesInConsentsMarker() {
    return this.modifiedConsentsGranted + this.modifiedConsentsRevoked * 100;
  }

  get customerAttributesOrigin() {
    return (this.coms4csState.getInitialSnapshot()?.customerAttributes ?? []) as CustomerAttribute[];
  }

  get modifiedCustomerAttributesGranted() {
    const diff = customerAttributesChanges(
      this.customerAttributesOrigin ? this.customerAttributesOrigin : [],
      this.customerAttributes ? this.customerAttributes : [],
    );

    return diff.granted;
  }

  get modifiedCustomerAttributesRevoked() {
    const diff = customerAttributesChanges(
      this.customerAttributesOrigin ? this.customerAttributesOrigin : [],
      this.customerAttributes ? this.customerAttributes : [],
    );

    return diff.revoked;
  }

  get changesEverywhereMarker() {
    return this.modifiedConsentsGranted
      + this.modifiedConsentsRevoked * 100
      + this.modifiedCustomerAttributesGranted * 1000
      + this.modifiedCustomerAttributesRevoked * 10000;
  }

  /**
   * Checks if all consents within the consents array have been either revoked or granted.
   *
   * @return {boolean} Returns true if all consents are either revoked or granted, otherwise false.
   */
  get allConsentsRevokedOrGranted() {
    return this.consents.every((consent) => consent.isRevokedOrGranted);
  }

  reset() {
    runInAction(() => {
      this.config = undefined;
      this.client = undefined;
      this.history = undefined;
      this.occl = undefined;
      this.fromStorage = false;
      this.showReferences = false;
      this.consentGroups = undefined;
      this.customerAttributes = [];
      this.identity = undefined;
      this.error = undefined;
      this.references = [];
      this.showVersions = false;
      this.isExistingRegister = false;
      this.isNewCustomer = false;
    });
  }

  /** indicating a fatal error condition when starting up <tef-coms4cs> */
  public get fatal(): Error | undefined {
    return this.error
      ?? this.occl?.error
      ?? (this.isDataLoaded ? undefined : this.client?.error);
  }

  public async init(config: TefComs.ConfigOptions): Promise<void> {
    this.error = undefined; // reset error state -> important for browser tests

    this.config = {
      ...config,
      apiUri: config.apiUri?.replace(/\/$/, ''),
      occlUri: config.occlUri?.replace(/\/$/, ''),
      channel: config.channel || 'Shop',
      usertype: config.usertype || 'Agent',
      consentwhitelist: config.consentwhitelist?.trim(),
      references: config.references,
      showReferences: config.showReferences || false,
      showVersions: config.showVersions || false,
      isExistingRegister: config.isExistingRegister || false,
      isNewCustomer: config.isNewCustomer || false,
      registerId: config.registerId,
    };

    const once = autorun(() => {
      if (this.isDataLoaded && this.isOcclReady) {
        this.service.fireEvent<TefComs.EventDetails>({
          name: 'load/READY',
          level: 'info',
          message: 'micro-frontend is ready',
        });
        once();
      }
      if (this.fatal) {
        this.service.fireEvent<TefComs.EventDetails>({
          name: 'load/ERROR',
          level: 'error',
          error: this.fatal,
        });
        this.tracker.comsError(this.fatal.message);
        once();
      }
    });

    if (this.config.references) {
      try {
        this.references = JSON.parse(this.config.references) as TefComs.Reference[];
        this.references.forEach((ref) => {
          if (!isReference(ref)) {
            const refError = new Error();
            refError.message = 'invalid object structure';
            throw refError;
          }
        });
      } catch (error) {
        const errorObject = error as Error;
        const formatError = new Error(`attribute "references" invalid format: ${errorObject.message}`);
        this.error = formatError;
        throw formatError;
      }
    }

    // validate consentwhitelist
    if (this.config.consentwhitelist) {
      try {
        this.consentWhitelist = JSON.parse(this.config.consentwhitelist) as string[];
      } catch (error) {
        const errorObject = error as Error;
        const formatError = new Error(`attribute "consentwhitelist" invalid JSON format: ${errorObject.message}`);
        this.error = formatError;
        throw formatError;
      }
    }

    try {
      assert(
        !this.consentWhitelist || Array.isArray(this.consentWhitelist),
        'attribute "consentwhitelist" is not an array',
      );
      assert(
        !this.consentWhitelist || this.consentWhitelist.reduce<boolean>((acc, s) => acc && isUuid(s), true),
        'attribute "consentwhitelist" contains invalid UUIDs',
      );
      assert(!!this.config.apiUri, 'attribute "apiuri" is not set');

      if (!this.config.agentToken) {
        assert(!!this.config.spId, 'attribute "spId" is not set');
        assert(!!this.config.lcId || !!this.config.registerId, 'attribute "lcId" or "registerId" is not set');
      } else {
        assert(!!this.config.agentToken, 'attribute "agenttoken" is not set');
      }

      assert(!!this.config.lifecycle, 'attribute "lifecycle" is not set');
      assert(!this.config.mode || ['overview', 'buttons', 'buttons:undoAll'].includes(this.config.mode), 'attribute "mode" is wrong');
    } catch (error) {
      this.error = error as Error;
      throw error;
    }

    Tracker.init(this.config);

    this.client = new ClientStore(this.config);
    if (this.config.occlUri) {
      this.occl = new OcclStore(this.config.occlUri, this.config.styleContext);
    }
    this.history = new HistoryStore(this.client);

    const identity = await this.client.getIdentity();
    runInAction(() => {
      this.identity = identity;
    });

    await this.loadConsents();

    if (this.fromStorage && this.hasModifiedFromInitial) {
      this.touched = true;
    }

    autorun(() => {
      if (this.isDataLoaded) {
        this.coms4csState.saveSnapshot();
      }
    });

    autorun(() => {
      this.triggerConsentsAllDefinedEvent();
    });

    // react on any changes in consents
    reaction(() => this.changesInConsentsMarker, () => {
      this.triggerConsentsChangeEvent();
    });

    reaction(() => this.changesEverywhereMarker, () => {
      this.touched = true;
      this.triggerConsentsStatusEvent();
    });
  }

  /**
   * Retrieves the status message related to consent changes based on granted or revoked permissions.
   *
   * The method evaluates the counters for granted and revoked changes to determine the appropriate message:
   * - Returns 'additional_grants' if there are more than 0 granted permissions.
   * - Returns 'only_revokes' if there are more revoked permissions.
   * - Defaults to 'no_changes' if no significant changes are detected.
   *
   * @return {string} The consent status message which could be 'additional_grants', 'only_revokes', or 'no_changes'.
   */
  get consentsStatusMessage(): string {
    let eventMessage = 'no_changes';

    const grantedNumber = this.modifiedCustomerAttributesGranted + this.modifiedConsentsGranted;
    const revokedNumber = this.modifiedCustomerAttributesRevoked + this.modifiedConsentsRevoked;

    if (grantedNumber > 0) {
      eventMessage = 'additional_grants';
    } else if (revokedNumber > 0) {
      eventMessage = 'only_revokes';
    }

    return eventMessage;
  }

  // will be fired only if consentsStatusMessage changed
  triggerConsentsStatusEvent() {
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/STATUS',
      level: 'info',
      message: this.consentsStatusMessage,
    });
  }

  // will be fired only if allConsentsRevokedOrGranted changed and initial
  triggerConsentsAllDefinedEvent() {
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/ALLDEFINED',
      level: 'info',
      message: String(this.allConsentsRevokedOrGranted),
    });
  }

  triggerConsentsChangeEvent() {
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/CHANGE',
      level: 'info',
      message: 'user changed a consent',
    });
  }

  get isDataLoaded(): boolean {
    return !!this.consentGroups;
  }

  get isOcclReady(): boolean {
    return (this.occl) ? this.occl.ready : true;
  }

  setCustomerAttribute(customerAttribute: CustomerAttribute): void {
    const remainingCustomerAttributes = this.customerAttributes?.filter(
      (cattr) => cattr.id !== customerAttribute.id,
    ) ?? [];

    this.customerAttributes = [
      ...remainingCustomerAttributes,
      customerAttribute,
    ];
    renderedPortalCache.clear();
  }

  /** Flat list of consents. */
  get consents(): ConsentStore[] {
    if (!this.consentGroups) return [];
    return this.consentGroups.flatMap((group) => group.consents);
  }

  public findGroupByName(groupName: string): ConsentGroupStore | undefined {
    return this.consentGroups?.find((group) => group.name === groupName);
  }

  public async save(): Promise<void> {
    const refs = this.references.filter((r) => !!r.id);

    const consentsData = this.consents.map((consent) => consent.toSaveConsent());
    const references = refs.length > 0 ? refs.map(({ state, ...ref }) => ref) : undefined;

    await this.client?.saveConsents(
      consentsData,
      references,
      this.customerAttributes,
    );

    this.tracker.changePermissions(this.consentGroups ?? []);
    this.coms4csState.reset();
  }

  loadSnapshot({ customerAttributes, details, groups }: ApiLoadConsentResponse) {
    this.customerAttributes = customerAttributes;
    this.details = details;
    this.consentGroups = groups.map((group) => new ConsentGroupStore(group, this.fromStorage));
    renderedPortalCache.clear();
  }

  public undo() {
    const previousSnapshot = this.coms4csState.getPreviousSnapshot();
    if (previousSnapshot) {
      this.coms4csState.removeLastSnapshots(2);
      this.fromStorage = true;
      this.loadSnapshot(previousSnapshot);
    }
  }

  undoAll() {
    const initialSnapshot = this.coms4csState.getInitialSnapshot();
    if (initialSnapshot) {
      this.coms4csState.reset();
      this.fromStorage = true;
      this.loadSnapshot(initialSnapshot);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public cancel() {
    this.coms4csState.reset();
  }

  public getShortConsents(): TefComs.ShortConsent[] {
    return this.consents.map((consent) => consent.toShortConsent());
  }

  // This is used by EPOS to get the consents payload for PATCH /consent/sp/{spId}/register
  // That endpoint will return the necessary payload to render documents, eg. those that
  // the customer must sign in person in the store.
  public getConsentStatus(): TefComs.ConsentAndCustomerAttributeSaveType[] {
    let customerAttributeConsents : CustomerAttributeSave[] = [];
    if (this.customerAttributes) {
      customerAttributeConsents = this.customerAttributes.map((cattr) => mapCustomerAttributeTypeToSaveType(cattr));
    }
    const consents = this.consents.map((consent) => consent.toSaveConsent());
    return [...consents, ...customerAttributeConsents];
  }

  public async loadConsents() {
    if (!this.client) return;
    const consentsFromSession = this.$getConsentsFromSession();
    if (consentsFromSession) {
      this.coms4csState.removeLastSnapshots(1);
    } else {
      this.coms4csState.reset();
    }

    this.fromStorage = !!consentsFromSession;
    const { groups, details, customerAttributes } = consentsFromSession ?? await this.client.loadConsents();

    // filter consentGroups whith whitelist if consentwhitelist is set
    // and groups are not loaded from session
    let filteredGroups: ConsentGroup[] = groups;
    if (this.consentWhitelist && !this.fromStorage) {
      filteredGroups = groups
        .map((group) => ({
          ...group,
          consents: group.consents.filter((consent) => this.consentWhitelist?.includes(consent.id)),
        }))
        .filter((group) => group.consents.length > 0);
    }

    this.loadSnapshot({
      groups: filteredGroups,
      details,
      customerAttributes,
    });

    if (consentsFromSession) {
      // If the client.loadConsents is called, it fires "consents/LOAD" on its own so we only need to do this
      // if we restored the data from the session storage
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/LOAD',
        level: 'info',
        message: 'loading consents successful',
      });
    }

    if (this.consentGroups?.length === 0) {
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/EMPTY',
        level: 'info',
        message: 'no consents to be rendered',
      });
    }
  }

  getPageSnapshot(): ApiLoadConsentResponse {
    return {
      groups: this.consentGroups?.map((cg) => cg.toJSON()) ?? [],
      details: this.details ?? {},
      customerAttributes: this.customerAttributes ?? [],
    };
  }

  private $getConsentsFromSession(): ApiLoadConsentResponse | undefined {
    try {
      const dataInStorage = this.coms4csState.getState();
      if (!dataInStorage) return undefined;
      const match = (): boolean => (
        dataInStorage.config.lifecycle === this.config?.lifecycle
        && dataInStorage.config.gatewayToken === this.config?.gatewayToken
        && dataInStorage.config.apiUri === this.config?.apiUri
        && dataInStorage.identity.username === this.identity?.username
        && dataInStorage.identity.lcId === this.identity?.lcId
        && dataInStorage.identity.spId === this.identity?.spId
        && dataInStorage.identity.registerId === this.identity?.registerId
        && dataInStorage.identity.readonly === this.identity?.readonly
        && dataInStorage.config.lcId === this.config?.lcId
        && dataInStorage.config.registerId === this.config?.registerId
        && dataInStorage.config.spId === this.config?.spId
      );
      return match() ? this.coms4csState.getLastSnapshot() : undefined;
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (error) { // ignore error
      return undefined;
    }
  }
}

const comsStore = new ComsStore();

export default comsStore;
